// ============================================================
// HARMONIC CHARTER — Cloud Sync
// ============================================================

import { supabase } from '../lib/supabase';
import type { Song, HChartFile, SyncStatus } from '../core/types';
import { saveSong, loadSong, loadIndex, saveIndex } from '../core/library';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { Presence } from '../store/collaborationStore';
import { presenceColor } from '../store/collaborationStore';
import { v4 as uuid } from 'uuid';

const FILE_VERSION = '1.0';

// ------------------------------------------------------------
// SYNC STATUS — reactive singleton
// ------------------------------------------------------------

type StatusListener = (status: SyncStatus) => void;

let _status: SyncStatus = 'idle';
const _listeners = new Set<StatusListener>();

function setStatus(s: SyncStatus) {
  _status = s;
  _listeners.forEach((fn) => fn(s));
}

export function getSyncStatus(): SyncStatus { return _status; }

export function subscribeSyncStatus(fn: StatusListener): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ------------------------------------------------------------
// PUSH: local → cloud
// ------------------------------------------------------------

export async function pushSong(
  song: Song,
  userId: string,
  folderId?: string | null,
): Promise<{ ok: boolean; error?: string }> {
  if (!navigator.onLine) { setStatus('offline'); return { ok: false, error: 'Offline' }; }
  setStatus('syncing');

  let resolvedFolderId = folderId;
  if (resolvedFolderId === undefined) {
    const index = loadIndex();
    const meta = index.songs.find((s) => s.id === song.id);
    resolvedFolderId = meta?.folderId ?? null;
  }

  const file: HChartFile = { version: FILE_VERSION, song };
  const { error } = await supabase
    .from('songs')
    .upsert(
      {
        id:         song.id,
        owner_id:   userId,
        title:      song.title,
        data:       file,
        updated_at: song.updatedAt,
        folder_id:  resolvedFolderId,
      },
      { onConflict: 'id' },
    );

  if (error) {
    if (error.code === 'PGRST301' || error.message.includes('JWT')) { setStatus('idle'); }
    else { setStatus('error'); }
    return { ok: false, error: error.message };
  }
  setStatus('idle');
  return { ok: true };
}

// ------------------------------------------------------------
// UPDATE FOLDER ASSIGNMENT ONLY
// ------------------------------------------------------------

export async function updateSongFolderAssignment(
  songId: string,
  folderId: string | null,
): Promise<void> {
  if (!navigator.onLine) return;
  const { error } = await supabase
    .from('songs')
    .update({ folder_id: folderId })
    .eq('id', songId);

  if (error) console.error('[cloudSync] updateSongFolderAssignment error:', error.message);
}

// ------------------------------------------------------------
// PULL: cloud → local
// ------------------------------------------------------------

export async function pullSongs(): Promise<{ ok: boolean; error?: string }> {
  if (!navigator.onLine) { setStatus('offline'); return { ok: false, error: 'Offline' }; }
  setStatus('syncing');

  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('songs')
    .select('id, title, owner_id, data, created_at, updated_at, folder_id')
    .order('updated_at', { ascending: false });

  if (error) {
    if (error.code === 'PGRST301' || error.message.includes('JWT') || error.message.includes('403')) {
      setStatus('idle');
    } else { setStatus('error'); }
    return { ok: false, error: error.message };
  }

  if (!data) { setStatus('idle'); return { ok: true }; }

  const index = loadIndex();

  for (const row of data) {
    const file = row.data as HChartFile;
    if (!file?.song) continue;

    const local = loadSong(row.id);
    const cloudUpdated = new Date(row.updated_at).getTime();
    const localUpdated = local?.song?.updatedAt ? new Date(local.song.updatedAt).getTime() : 0;

    if (cloudUpdated >= localUpdated) {
      saveSong(file.song);
      const existing = index.songs.find((s) => s.id === row.id);
      if (!existing) {
        index.songs.push({
          id:         row.id,
          title:      row.title,
          updatedAt:  row.updated_at,
          createdAt:  row.created_at,
          folderId:   row.folder_id ?? null,
          sharedWith: user && row.owner_id !== user.id ? row.owner_id : undefined,
        });
      } else {
        existing.folderId   = row.folder_id ?? null;
        existing.sharedWith = user && row.owner_id !== user.id ? row.owner_id : undefined;
      }
    }
  }

  saveIndex(index);
  setStatus('idle');
  return { ok: true };
}

// ------------------------------------------------------------
// FORK
// ------------------------------------------------------------

export async function forkSong(
  originalSongId: string,
): Promise<{ ok: boolean; song?: Song; error?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'Not signed in' };

  const { data, error } = await supabase
    .from('songs')
    .select('data, title')
    .eq('id', originalSongId)
    .single();

  if (error || !data?.data) return { ok: false, error: error?.message ?? 'Song not found' };

  const file = data.data as HChartFile;
  if (!file?.song) return { ok: false, error: 'Invalid song data' };

  const forked: Song = {
    ...file.song,
    id:        uuid(),
    title:     `${file.song.title} (copy)`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  saveSong(forked);
  const index = loadIndex();
  index.songs.push({
    id:        forked.id,
    title:     forked.title,
    updatedAt: forked.updatedAt,
    createdAt: forked.createdAt,
    folderId:  null,
  });
  saveIndex(index);

  await pushSong(forked, user.id);
  return { ok: true, song: forked };
}

// ------------------------------------------------------------
// DELETE
// ------------------------------------------------------------

export async function deleteSongFromCloud(songId: string): Promise<void> {
  await supabase.from('songs').delete().eq('id', songId);
}

// ------------------------------------------------------------
// REALTIME: linked write mode
// ------------------------------------------------------------

let _channel: RealtimeChannel | null = null;

type ChartUpdateHandler   = (song: Song) => void;
type PresenceUpdateHandler = (presence: Presence) => void;
type PresenceLeaveHandler  = (userId: string) => void;

export async function subscribeToChart(
  songId: string,
  onChartUpdate: ChartUpdateHandler,
  onPresenceUpdate: PresenceUpdateHandler,
  onPresenceLeave: PresenceLeaveHandler,
): Promise<void> {
  await unsubscribeFromChart();

  _channel = supabase.channel(`chart:${songId}`, {
    config: { broadcast: { self: false } },
  });

  _channel
    .on('broadcast', { event: 'chart_update' },    ({ payload }) => {
      if (payload?.song) onChartUpdate(payload.song as Song);
    })
    .on('broadcast', { event: 'presence_update' }, ({ payload }) => {
      if (payload?.presence) onPresenceUpdate(payload.presence as Presence);
    })
    .on('broadcast', { event: 'presence_leave' },  ({ payload }) => {
      if (payload?.userId) onPresenceLeave(payload.userId as string);
    })
    .subscribe();
}

// FIX: accepts an optional `payload` argument so callers can pass a
// stripped version of the song (without local-only fields like
// displayMode and analysisSettings) without those fields being
// broadcast to and overwriting collaborators' personal preferences.
// When omitted, the full song is broadcast as before.
export async function broadcastChartUpdate(
  songId: string,
  song: Song,
  payload?: Song,
): Promise<void> {
  if (!_channel) return;
  await _channel.send({
    type:    'broadcast',
    event:   'chart_update',
    payload: { song: payload ?? song },
  });
}

export async function broadcastPresence(
  songId: string,
  userId: string,
  email: string,
  barId: string | null,
): Promise<void> {
  if (!_channel) return;
  const presence: Presence = {
    userId,
    email,
    color:    presenceColor(userId),
    barId,
    lastSeen: Date.now(),
  };
  await _channel.send({ type: 'broadcast', event: 'presence_update', payload: { presence } });
}

export async function broadcastLeave(songId: string, userId: string): Promise<void> {
  if (!_channel) return;
  await _channel.send({ type: 'broadcast', event: 'presence_leave', payload: { userId } });
}

export async function unsubscribeFromChart(): Promise<void> {
  if (_channel) {
    await supabase.removeChannel(_channel);
    _channel = null;
  }
}

// ------------------------------------------------------------
// COLLABORATORS
// ------------------------------------------------------------

export async function inviteCollaborator(
  songId: string,
  ownerId: string,
  email: string,
  mode: 'view' | 'write' | 'linked',
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('song_collaborators')
    .insert({
      song_id:  songId,
      owner_id: ownerId,
      email:    email.toLowerCase().trim(),
      mode,
      role: mode === 'view' ? 'viewer' : 'editor',
    });

  if (error) {
    if (error.code === '23505') return { ok: false, error: 'This person has already been invited.' };
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function removeCollaborator(
  songId: string,
  collaboratorId: string,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('song_collaborators')
    .delete()
    .eq('id', collaboratorId)
    .eq('song_id', songId);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function getCollaborators(songId: string) {
  const { data, error } = await supabase
    .from('song_collaborators')
    .select('*')
    .eq('song_id', songId)
    .order('invited_at', { ascending: true });

  return { data: data ?? [], error };
}

export async function acceptInvite(
  collaboratorId: string,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('song_collaborators')
    .update({ accepted: true })
    .eq('id', collaboratorId);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function getPendingInvites() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) return { data: [], error: null };

  const { data: invites, error } = await supabase
    .from('song_collaborators')
    .select('id, song_id, email, role, mode, invited_at')
    .eq('email', user.email.toLowerCase())
    .eq('accepted', false)
    .order('invited_at', { ascending: false });

  if (error || !invites?.length) return { data: [], error };

  const songIds = invites.map((i) => i.song_id);
  const { data: songs } = await supabase
    .from('songs')
    .select('id, title')
    .in('id', songIds);

  const titleMap = Object.fromEntries((songs ?? []).map((s) => [s.id, s.title]));

  const enriched = invites.map((invite) => ({
    ...invite,
    songs: { title: titleMap[invite.song_id] ?? 'Untitled' },
  }));

  return { data: enriched, error: null };
}

// ------------------------------------------------------------
// GET MODE FOR CURRENT USER ON A SONG
// ------------------------------------------------------------

export async function getMyCollaborationMode(
  songId: string,
): Promise<'view' | 'write' | 'linked' | 'owner' | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: songData } = await supabase
    .from('songs')
    .select('owner_id')
    .eq('id', songId)
    .single();

  if (songData?.owner_id === user.id) return 'owner';

  const { data } = await supabase
    .from('song_collaborators')
    .select('mode')
    .eq('song_id', songId)
    .eq('email', user.email ?? '')
    .eq('accepted', true)
    .maybeSingle();

  return (data?.mode as 'view' | 'write' | 'linked') ?? null;
}